// src/components/SearchBar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '../images/search-icon.svg';
import SearchSuggestions from './SearchSuggestions';
import './SearchBar.css';

function SearchBar({ onSearch, value = '', onClear }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchFieldRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [mobileSearchActive, setMobileSearchActive] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
    setShowSuggestions(true);
  };

  useEffect(() => {
    if (!value) {
      setSearchQuery('');
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [value]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setShowSuggestions(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchIconClick = () => {
    setMobileSearchActive(prev => {
      if (prev) {
        setShowSuggestions(false);
        return false;
      }
      return true;
    });
  };

  useEffect(() => {
    if (mobileSearchActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mobileSearchActive]);

  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    if (trimmed) {
      onSearch?.({ query: trimmed, field: 'title' });
      setShowSuggestions(false);
      setMobileSearchActive(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    if (suggestion.type === 'movie' || suggestion.type === 'upcoming') {
      navigate(`/movie/${suggestion.id}`);
    } else {
      const typeToField = {
        Director: 'director',
        Actor: 'cast_members',
        'Production Company': 'production_companies',
        Writer: 'writers',
        Producer: 'producers',
        Cinematographer: 'director_of_photography',
        'Music Composer': 'music_composer',
      };
      const field = typeToField[suggestion.type] || 'all';
      onSearch?.({ query: suggestion.name, field });
    }
    setShowSuggestions(false);
    setMobileSearchActive(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSuggestions &&
        searchFieldRef.current &&
        !searchFieldRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setMobileSearchActive(false);
        if (onClear && !searchQuery.trim()) {
          onClear();
        }
      }
    };
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, searchQuery, onClear]);

  return (
    <div
      className={`search-bar ${mobileSearchActive ? 'mobile-active' : ''}`}
      ref={searchFieldRef}
    >
      {/* Search Input */}
      <input
        ref={inputRef}
        type="text"
        onFocus={handleFocus}
        placeholder="Search movie, people, studio..."
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        className="search-input"
      />

      {/* Search Icon */}
      <button
        type="button"
        className="search-icon-button"
        onClick={handleSearchIconClick}
        aria-label="Search"
      >
        <img
          src={SearchIcon}
          alt=""
          className="search-icon"
          aria-hidden="true"
        />
      </button>

      {/* Search Suggestions */}
      {showSuggestions && (
        <SearchSuggestions
          query={searchQuery}
          searchField="all"
          onSelect={handleSuggestionSelect}
          visible={isFocused}
          onClose={() => setShowSuggestions(false)}
          inputRef={inputRef}
        />
      )}
    </div>
  );
}

export default SearchBar;
